import React from 'react';

// Errors that indicate the browser is running a stale build (e.g. a returning
// visitor whose old service worker served an index.html referencing JS chunks
// that no longer exist after a deploy). These are self-healable by clearing
// caches + unregistering the SW and reloading.
const STALE_BUILD_ERR = /(ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unexpected token ['"<])/i;

export function clearStaleCaches() {
  const tasks = [];
  try {
    if (window.caches && caches.keys) {
      tasks.push(caches.keys().then((ks) => Promise.all(ks.map((k) => caches.delete(k)))).catch(() => {}));
    }
    if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
      tasks.push(
        navigator.serviceWorker.getRegistrations()
          .then((rs) => Promise.all(rs.map((r) => r.unregister())))
          .catch(() => {})
      );
    }
  } catch (e) { /* ignore */ }
  return Promise.all(tasks);
}

/**
 * App-wide error boundary. Prevents the "white screen of death": instead of an
 * uncaught render error unmounting the whole React tree to a blank page, the
 * user sees a branded recovery screen. Stale-deploy/chunk errors auto self-heal
 * once (clear caches + unregister SW + reload), guarded against reload loops.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    const msg = (error && (error.message || String(error))) || '';
    if (STALE_BUILD_ERR.test(msg) && !sessionStorage.getItem('cl_recovered')) {
      try { sessionStorage.setItem('cl_recovered', '1'); } catch (e) { /* ignore */ }
      this.setState({ recovering: true });
      clearStaleCaches().finally(() => window.location.reload());
    }
  }

  handleReload = () => {
    clearStaleCaches().finally(() => window.location.reload());
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const wrap = {
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#F7F5F0', color: '#0F172A',
      fontFamily: 'Manrope, system-ui, -apple-system, sans-serif', padding: 24, textAlign: 'center',
    };

    if (this.state.recovering) {
      return (
        <div style={wrap} data-testid="app-error-recovering">
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Updating CaraLoom…</div>
            <p style={{ color: '#5b6b64', marginTop: 6 }}>One moment — getting the latest version.</p>
          </div>
        </div>
      );
    }

    return (
      <div style={wrap} data-testid="app-error-boundary">
        <div style={{ maxWidth: 440 }}>
          <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Something went wrong</div>
          <p style={{ color: '#5b6b64', margin: '0 0 24px', lineHeight: 1.55 }}>
            We hit an unexpected error. Reloading usually fixes it. If it keeps happening,
            please contact CaraLoom support.
          </p>
          <button
            onClick={this.handleReload}
            data-testid="error-reload-btn"
            style={{ background: '#0F172A', color: '#fff', border: 'none', borderRadius: 999, padding: '12px 28px', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
          >
            Reload CaraLoom
          </button>
          <div style={{ marginTop: 16 }}>
            <a href="/" style={{ color: '#0F172A', fontSize: 14, textDecoration: 'underline' }}>Go to homepage</a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
